import { format } from "date-fns";

import { getOrganizationCarbonEmissions } from "Api/carbonEmissions/getOrganizationCarbonEmissions.request";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { setOrganizationLoading } from "../carbonEmissions.reducer";
import { normalizeCarbonEmissionsData } from "../normalizeInput";

// Define a custom error payload type that includes organizationId
interface ErrorPayload {
  error: string;
  organizationId?: string;
}

const createErrorPayload = (error: unknown): ErrorPayload => ({
  error: error instanceof Error ? error.message : "An unknown error occurred"
});

// Helper function to format dates for API
const formatDate = (date: Date): string => format(date, "yyyy-MM-dd");

export const fetchPreviousYearMonthlyCarbonEmissionsThunk = createAppAsyncThunk(
  "carbonEmissions/fetchPreviousYearMonthlyCarbonEmissions",
  async (organizationId: string, { rejectWithValue, dispatch }) => {
    try {
      // Important change: Set loading state using organizationId, not organizationName
      dispatch(
        setOrganizationLoading({
          organizationId, // Use ID, not name
          isLoading: true
        })
      );

      // Get dates for previous year but extend to beginning of current year
      const now = new Date();
      const previousYear = now.getFullYear() - 1;
      const currentYear = now.getFullYear();

      // Start date is Jan 1st of previous year
      const from = formatDate(new Date(previousYear, 0, 1));

      // End date is Jan 1st of current year (to include all of December)
      const to = formatDate(new Date(currentYear, 0, 1));

      const response = await getOrganizationCarbonEmissions({
        organizationId,
        from,
        to,
        interval: "month"
      });

      // Check if response has valid structure
      if (!response || !response.projects || response.projects.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("API returned empty or invalid carbon emissions data");
      }

      // Normalize the data with minimal transformation
      const normalizedData = normalizeCarbonEmissionsData(
        response,
        organizationId
      );

      // Return using organizationId for consistency
      return {
        organizationId, // Use ID consistently here
        data: normalizedData
      };
    } catch (error) {
      // Set loading to false if we have an ID
      if (organizationId) {
        dispatch(
          setOrganizationLoading({
            organizationId, // Use ID, not name
            isLoading: false
          })
        );
      }

      const errorPayload = createErrorPayload(error);
      if (organizationId) {
        errorPayload.organizationId = organizationId;
      }

      return rejectWithValue(errorPayload);
    }
  }
);
